/**
 * @fileoverview Slate Ink — Lyn Design System Engine
 * @module @liquidefy/lyn/global/visuals/styles/styles
 * @license @lqdresearch
 *
 * @author Scott Martin
 * @since 1.0.0
 * @tier Infrastructure
 * @rust styles.rs — false; 4/24/26; sm
 *
 * Purpose:
 *   Pure TypeScript engine for the Lyn design system.
 *   Built on the Slate Ink aesthetic — a dark blue-slate foundation
 *   with frosted glass surfaces, paired with a cool blue-gray light
 *   mode and a high-contrast accessibility mode.
 *
 *   Zero framework dependencies. Zero module-level mutable state.
 *   Every function pure. Every exported value frozen.
 *
 *   Consumed by Styles.svelte (the reactive wrapper) which holds
 *   $state for the active mode, binds recipes via bindRecipes(),
 *   and exposes the result through useStyles() context.
 *
 *   Design system: Slate Ink (DM Sans, blur 24px, rgba text, 0.5px borders).
 *
 * Architecture:
 *   styles.ts     — THIS FILE: tokens + recipes + utilities
 *   Styles.svelte — reactive wrapper: $state + Device integration +
 *                   localStorage + data-theme attribute + context
 *
 * Tier classification:
 *   Glacier — tokens are frozen on export; never mutate.
 *   Cold tier consumer — wrapper's activeMode $state drives token selection.
 */

// ══════════════════════════════════════════════════════════════════════
// 1. TYPES
// ══════════════════════════════════════════════════════════════════════

/**
 * The fully resolved visual rendering mode — what the UI is actually
 * displaying. High-contrast is its own mode (not a modifier on dark/light)
 * because the token values differ enough that treating it as "dark with
 * overrides" would be a leaky abstraction.
 */
export type ThemeMode = "dark" | "light" | "high-contrast";

/**
 * The user's explicit mode preference.
 * "system" means follow OS preference (prefers-color-scheme + prefers-contrast).
 * Anything else pins the mode regardless of OS settings.
 */
export type UserModePreference = ThemeMode | "system";

export interface SurfaceTokens {
  /** App background. */
  readonly base:    string;
  /** Elevated cards, inputs, raised panels. */
  readonly raised:  string;
  /** Recessed areas like sidebar, wells. */
  readonly sunken:  string;
  /** Header and other frosted overlay surfaces (with alpha). */
  readonly overlay: string;
}

export interface GlassTokens {
  /** Resting glass fill. */
  readonly fill:      string;
  /** Glass fill on hover. */
  readonly fillHover: string;
  /** Glass edge — full CSS border shorthand including width+style+color. */
  readonly border:    string;
  /** Backdrop-filter value, e.g. "blur(24px)". */
  readonly blur:      string;
}

export interface TextTokens {
  /** Headings, active labels. */
  readonly primary:     string;
  /** Body text, descriptions. */
  readonly secondary:   string;
  /** Captions, hints. */
  readonly tertiary:    string;
  /** Disabled text, watermarks. */
  readonly ghost:       string;
  /** Input placeholder text. */
  readonly placeholder: string;
}

export interface BorderTokens {
  /** Standard dividers. */
  readonly standard: string;
  /** Inner section lines. */
  readonly subtle:   string;
  /** Hover emphasis. */
  readonly strong:   string;
  /** Input outlines. */
  readonly input:    string;
  /** Focus ring. */
  readonly focus:    string;
}

export interface InteractiveTokens {
  /** Hover fill. */
  readonly hover:    string;
  /** Active/pressed fill. */
  readonly active:   string;
  /** Selected row fill. */
  readonly selected: string;
}

export interface IconTokens {
  /** Idle icon color. */
  readonly idle:  string;
  /** Icon color on hover or active parent. */
  readonly hover: string;
}

export interface AccentTokens {
  /** Solid accent color. */
  readonly solid:  string;
  /** Tinted background fills. */
  readonly subtle: string;
  /** Accent text color. */
  readonly text:   string;
  /** Accent outlines. */
  readonly border: string;
  /** Ambient glow shadow fill. */
  readonly glow:   string;
}

export interface SemanticTokens {
  readonly success:       string;
  readonly successSubtle: string;
  readonly warning:       string;
  readonly warningSubtle: string;
  readonly error:         string;
  readonly errorSubtle:   string;
}

export interface ShadowTokens {
  /** Subtle lift. */
  readonly sm:     string;
  /** Cards, menus. */
  readonly md:     string;
  /** Modals, popovers. */
  readonly lg:     string;
  /** Sticky header. */
  readonly header: string;
}

export interface SelectionTokens {
  /** ::selection background. */
  readonly background: string;
  /** ::selection text color. */
  readonly color:      string;
}

/**
 * The full token set for a single mode.
 * DARK, LIGHT, and HIGH_CONTRAST all satisfy this.
 */
export interface ThemeTokens {
  readonly mode:        ThemeMode;
  readonly surface:     SurfaceTokens;
  readonly glass:       GlassTokens;
  readonly text:        TextTokens;
  readonly border:      BorderTokens;
  readonly interactive: InteractiveTokens;
  readonly icon:        IconTokens;
  readonly accent:      AccentTokens;
  readonly semantic:    SemanticTokens;
  readonly shadow:      ShadowTokens;
  readonly selection:   SelectionTokens;
}

/** Generic style object — used as recipe return type for inline style application. */
export interface StyleProps {
  readonly [key: string]: string | number | undefined;
}

/** Recipe that returns a base style plus optional hover/active variants and CSS. */
export interface StyleRecipe {
  readonly style:        StyleProps;
  readonly hoverStyle?:  StyleProps;
  readonly activeStyle?: StyleProps;
  readonly css?:         string;
}

/**
 * All recipes pre-bound to a specific token set.
 * Styles.svelte calls bindRecipes(activeTokens) and exposes this via
 * context so components can call e.g. `recipes.glass.frost()` with
 * zero arguments — tokens are already baked in.
 */
export interface BoundRecipes {
  readonly glass: {
    readonly frost:       () => StyleProps;
    readonly heavy:       () => StyleProps;
    readonly subtleMist:  () => StyleProps;
    readonly topDown:     () => StyleProps;
    readonly bottomUp:    () => StyleProps;
    readonly mixed:       () => StyleProps;
    readonly warm:        () => StyleProps;
    readonly pure:        () => StyleProps;
    readonly centerGlow:  () => StyleProps;
    readonly edgeHalo:    () => StyleProps;
    readonly diagonal:    () => StyleProps;
    readonly cornerSpot:  () => StyleProps;
    readonly reverseHex:  () => StyleProps;
    readonly horizontal:  () => StyleProps;
    readonly accentTint:  () => StyleProps;
    readonly hex:         () => StyleProps;
  };
  readonly surface: {
    readonly base:         () => StyleProps;
    readonly sidebar:      () => StyleProps;
    readonly sidebarGlow:  () => StyleProps;
    readonly sidebarBlur:  () => StyleProps;
    readonly header:       () => StyleProps;
    readonly input:        () => StyleProps;
    readonly inputFocused: () => StyleProps;
    readonly flat:         () => StyleProps;
  };
  readonly button: {
    readonly primary:   () => StyleRecipe;
    readonly secondary: () => StyleRecipe;
    readonly ghost:     () => StyleRecipe;
    readonly glass:     () => StyleRecipe;
    readonly icon:      () => StyleRecipe;
    readonly tool:      () => StyleRecipe;
  };
  readonly avatar: {
    readonly active: (size: number) => StyleProps;
    readonly away:   (size: number) => StyleProps;
    readonly collab: (size: number) => StyleProps;
  };
  readonly state: {
    readonly idle:     () => StyleProps;
    readonly hover:    () => StyleProps;
    readonly active:   () => StyleProps;
    readonly selected: () => StyleProps;
    readonly focused:  () => StyleProps;
    readonly disabled: () => StyleProps;
  };
  readonly skeleton: {
    readonly line:          () => StyleProps;
    readonly shimmer:       () => StyleProps;
    readonly collabShimmer: () => StyleProps;
  };
  readonly nav: {
    readonly item:     () => StyleRecipe;
    readonly listItem: () => StyleRecipe;
  };
  readonly dropdown: {
    readonly container: () => StyleProps;
    readonly item:      () => StyleRecipe;
    readonly separator: () => StyleProps;
  };
  readonly brand: {
    readonly name:    () => StyleProps;
    readonly tagline: () => StyleProps;
    readonly hover:   () => StyleProps;
  };
  readonly scrollWheel: {
    readonly tick:    () => StyleProps;
    readonly body:    () => StyleProps;
    readonly physics: () => typeof SCROLL_WHEEL;
  };
  readonly type: {
    readonly xl:    () => StyleProps;
    readonly lg:    () => StyleProps;
    readonly md:    () => StyleProps;
    readonly body:  () => StyleProps;
    readonly sm:    () => StyleProps;
    readonly xs:    () => StyleProps;
    readonly label: () => StyleProps;
  };
  readonly table: {
    readonly container:           () => StyleProps;
    readonly toolbar:             () => StyleProps;
    readonly colHead:             () => StyleProps;
    readonly colHeadHover:        () => StyleProps;
    readonly colHeadSelected:     () => StyleProps;
    readonly colHeadSelectedHover:() => StyleProps;
    readonly rowNum:              () => StyleProps;
    readonly cellEven:            () => StyleProps;
    readonly cellOdd:             () => StyleProps;
    readonly cellHighlight:       () => StyleProps;
    readonly selectionOverlay:    () => StyleProps;
    readonly activeShimmer:       () => StyleProps;
    readonly statStrip:           () => StyleProps;
    readonly sheetTab:            () => StyleProps;
    readonly sheetTabActive:      () => StyleProps;
    readonly dashPanel:           () => StyleProps;
  };
  readonly selection: () => SelectionTokens;
}


// ══════════════════════════════════════════════════════════════════════
// 2. SHARED CONSTANTS (mode-independent)
// ══════════════════════════════════════════════════════════════════════

/** Border radii. */
export const RADIUS = {
  xs:   "4px",
  sm:   "6px",
  md:   "8px",
  lg:   "12px",
  xl:   "16px",
  pill: "999px",
} as const;

/** Spacing scale — 4px base. */
export const SPACE = {
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
} as const;

/** Layout dimensions (numeric — for math). */
export const LAYOUT = {
  headerH:     56,
  sidebarW:    280,
  sidebarRail: 48,
  inputH:      72,
  readingW:    720,
} as const;

/** Typography. */
export const TYPE = {
  font:     "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  mono:     "'SF Mono', 'Fira Code', monospace",
  tracking: "-0.012em",
  leading:  "1.6",
} as const;

/** Easing curves. */
export const EASE = {
  fast:   "150ms ease",
  normal: "250ms ease",
  smooth: "400ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  spring: "500ms cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

/** Scroll wheel physics constants. */
export const SCROLL_WHEEL = {
  width:            18,
  height:           72,
  tickSpacing:      10,
  tickCount:        20,
  tickHeight:       2.5,
  tickInset:        3,
  fadeDelay:        1400,
  rotationMult:     0.15,
  throwFriction:    0.94,
  throwMinVelocity: 10,
} as const;

/**
 * Hex button gradient — shared reference across avatar, button.hex, etc.
 * Mode-independent because the hex visual is a dark gradient regardless.
 */
export const HEX_GRADIENT = {
  fill:      "linear-gradient(180deg, rgba(42,42,52,0.72) 0%, rgba(28,28,36,0.68) 100%)",
  fillHover: "linear-gradient(180deg, rgba(42,42,52,0.88) 0%, rgba(28,28,36,0.82) 100%)",
  border:    "0.5px solid rgba(255,255,255,0.10)",
} as const;

/** Chart series colors — 5 colors for data visualization. */
export const SERIES_COLORS = {
  dark:          ["#60a5fa", "#5eead4", "#c4b5fd", "#fcd34d", "#fda4af"] as const,
  light:         ["#3a6ef5", "#14b8a6", "#8b5cf6", "#d97706", "#e11d48"] as const,
  highContrast:  ["#ffff00", "#00ffff", "#ff00ff", "#00ff00", "#ff8800"] as const,
} as const;


// ══════════════════════════════════════════════════════════════════════
// 3. DARK MODE TOKENS
// ──────────────────────────────────────────────────────────────────────
// Foundation: #15151b — dark blue-slate that reads near-black but
// carries enough hue to feel intentional. Glass: frosted white with
// heavy blur (24px), no shadows. Accent: #60a5fa blue, used sparingly.
// ══════════════════════════════════════════════════════════════════════

export const DARK: ThemeTokens = {
  mode: "dark",

  surface: {
    base:    "#15151b",
    raised:  "#1a1a21",
    sunken:  "#111116",
    overlay: "rgba(21,21,27,0.85)",
  },

  glass: {
    fill:      "rgba(255,255,255,0.04)",
    fillHover: "rgba(255,255,255,0.06)",
    border:    "0.5px solid rgba(255,255,255,0.08)",
    blur:      "blur(24px)",
  },

  text: {
    primary:     "rgba(255,255,255,0.92)",
    secondary:   "rgba(255,255,255,0.44)",
    tertiary:    "rgba(255,255,255,0.25)",
    ghost:       "rgba(255,255,255,0.15)",
    placeholder: "rgba(255,255,255,0.28)",
  },

  border: {
    standard: "rgba(255,255,255,0.08)",
    subtle:   "rgba(255,255,255,0.05)",
    strong:   "rgba(255,255,255,0.12)",
    input:    "rgba(255,255,255,0.10)",
    focus:    "rgba(255,255,255,0.22)",
  },

  interactive: {
    hover:    "rgba(255,255,255,0.04)",
    active:   "rgba(255,255,255,0.07)",
    selected: "rgba(255,255,255,0.06)",
  },

  icon: {
    idle:  "rgba(255,255,255,0.38)",
    hover: "rgba(255,255,255,0.88)",
  },

  accent: {
    solid:  "#60a5fa",
    subtle: "rgba(96,165,250,0.12)",
    text:   "rgba(96,165,250,0.90)",
    border: "rgba(96,165,250,0.20)",
    glow:   "rgba(96,165,250,0.08)",
  },

  semantic: {
    success:       "#34d399",
    successSubtle: "rgba(52,211,153,0.10)",
    warning:       "#fbbf24",
    warningSubtle: "rgba(251,191,36,0.10)",
    error:         "#f87171",
    errorSubtle:   "rgba(248,113,113,0.10)",
  },

  shadow: {
    sm:     "0 1px 4px rgba(0,0,0,0.15)",
    md:     "0 2px 8px rgba(0,0,0,0.20)",
    lg:     "0 4px 16px rgba(0,0,0,0.25)",
    header: "0 1px 6px rgba(0,0,0,0.20)",
  },

  selection: {
    background: "rgba(255,255,255,0.15)",
    color:      "inherit",
  },
};


// ══════════════════════════════════════════════════════════════════════
// 4. LIGHT MODE TOKENS
// ──────────────────────────────────────────────────────────────────────
// Cool blue-gray paper. Glass gets shadow in light mode (unlike dark).
// Sidebar is a subtle step down from base.
// ══════════════════════════════════════════════════════════════════════

export const LIGHT: ThemeTokens = {
  mode: "light",

  surface: {
    base:    "#f2f2f5",
    raised:  "#ffffff",
    sunken:  "#ededf0",
    overlay: "rgba(242,242,245,0.85)",
  },

  glass: {
    fill:      "linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(248,248,252,0.75) 100%)",
    fillHover: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,248,252,0.88) 100%)",
    border:    "0.5px solid rgba(0,0,0,0.06)",
    blur:      "blur(24px)",
  },

  text: {
    primary:     "rgba(0,0,0,0.88)",
    secondary:   "rgba(0,0,0,0.44)",
    tertiary:    "rgba(0,0,0,0.22)",
    ghost:       "rgba(0,0,0,0.12)",
    placeholder: "rgba(0,0,0,0.30)",
  },

  border: {
    standard: "rgba(0,0,0,0.08)",
    subtle:   "rgba(0,0,0,0.04)",
    strong:   "rgba(0,0,0,0.12)",
    input:    "rgba(0,0,0,0.10)",
    focus:    "rgba(0,0,0,0.24)",
  },

  interactive: {
    hover:    "rgba(0,0,0,0.03)",
    active:   "rgba(0,0,0,0.06)",
    selected: "rgba(0,0,0,0.05)",
  },

  icon: {
    idle:  "rgba(0,0,0,0.36)",
    hover: "rgba(0,0,0,0.84)",
  },

  accent: {
    solid:  "#3a6ef5",
    subtle: "rgba(58,110,245,0.10)",
    text:   "rgba(58,110,245,0.90)",
    border: "rgba(58,110,245,0.20)",
    glow:   "rgba(58,110,245,0.06)",
  },

  semantic: {
    success:       "#059669",
    successSubtle: "rgba(5,150,105,0.08)",
    warning:       "#d97706",
    warningSubtle: "rgba(217,119,6,0.08)",
    error:         "#dc2626",
    errorSubtle:   "rgba(220,38,38,0.08)",
  },

  shadow: {
    sm:     "0 1px 4px rgba(0,0,0,0.05)",
    md:     "0 2px 8px rgba(0,0,0,0.06)",
    lg:     "0 4px 16px rgba(0,0,0,0.08)",
    header: "0 1px 6px rgba(0,0,0,0.05)",
  },

  selection: {
    background: "rgba(0,0,0,0.12)",
    color:      "inherit",
  },
};


// ══════════════════════════════════════════════════════════════════════
// 5. HIGH-CONTRAST MODE TOKENS
// ──────────────────────────────────────────────────────────────────────
// Stub values — designed to compile and be visibly high-contrast.
// NOT yet WCAG AAA validated. Follows Slate Ink structure:
//   - DM Sans, same radii/spacing/layout (inherited from constants)
//   - Pure black surfaces, pure white text — maximum contrast
//   - Stronger accent blue (same family as dark, saturated for visibility)
//   - Yellow reserved for focus rings (maximum attention marker)
//   - No blur (reduces contrast), no shadows (reduces contrast)
//   - Opaque borders, 1px minimum (0.5px is hard to see at high contrast)
//
// Refine with real WCAG AAA testing before launch.
// ══════════════════════════════════════════════════════════════════════

export const HIGH_CONTRAST: ThemeTokens = {
  mode: "high-contrast",

  surface: {
    base:    "#000000",
    raised:  "#000000",
    sunken:  "#000000",
    overlay: "rgba(0,0,0,0.95)",
  },

  glass: {
    fill:      "#000000",
    fillHover: "#1a1a1a",
    border:    "1px solid #ffffff",
    blur:      "blur(0px)",
  },

  text: {
    primary:     "#ffffff",
    secondary:   "#ffffff",
    tertiary:    "#ffff00",
    ghost:       "#ffff00",
    placeholder: "#ffff00",
  },

  border: {
    standard: "#ffffff",
    subtle:   "#ffffff",
    strong:   "#ffffff",
    input:    "#ffffff",
    focus:    "#ffff00",
  },

  interactive: {
    hover:    "#1a1a1a",
    active:   "#333333",
    selected: "#0066cc",
  },

  icon: {
    idle:  "#ffffff",
    hover: "#ffff00",
  },

  accent: {
    solid:  "#4d9fff",
    subtle: "#001a33",
    text:   "#4d9fff",
    border: "#4d9fff",
    glow:   "transparent",
  },

  semantic: {
    success:       "#00ff00",
    successSubtle: "#002200",
    warning:       "#ffaa00",
    warningSubtle: "#221100",
    error:         "#ff3333",
    errorSubtle:   "#220000",
  },

  shadow: {
    sm:     "none",
    md:     "none",
    lg:     "none",
    header: "none",
  },

  selection: {
    background: "#ffff00",
    color:      "#000000",
  },
};


// ══════════════════════════════════════════════════════════════════════
// 6. UTILITY FUNCTIONS
// ══════════════════════════════════════════════════════════════════════

/** Get the token set for a mode. */
export function getTokens(mode: ThemeMode): ThemeTokens {
  switch (mode) {
    case "dark":          return DARK;
    case "light":         return LIGHT;
    case "high-contrast": return HIGH_CONTRAST;
  }
}

/** Convert camelCase to kebab-case. */
function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
}

/** Generate an inline CSS style string from a props object. */
export function styleString(props: Record<string, string | number>): string {
  return Object.entries(props)
    .map(([k, v]) => `${camelToKebab(k)}: ${v}`)
    .join("; ");
}

/** Build a border shorthand. */
export function border(width: number, color: string): string {
  return `${width}px solid ${color}`;
}

/** Build a backdrop-filter string. */
export function backdrop(blur: number, saturate?: number): string {
  let val = `blur(${blur}px)`;
  if (saturate !== undefined && saturate !== 1) val += ` saturate(${saturate})`;
  return val;
}

/** Build a linear-gradient string. */
export function gradient(angle: number, ...stops: Array<[string, string]>): string {
  const s = stops.map(([color, pos]) => `${color} ${pos}`).join(", ");
  return `linear-gradient(${angle}deg, ${s})`;
}

/** Build a radial-gradient string. */
export function radialGradient(position: string, ...stops: Array<[string, string]>): string {
  const s = stops.map(([color, pos]) => `${color} ${pos}`).join(", ");
  return `radial-gradient(ellipse at ${position}, ${s})`;
}

/** White at a given opacity — for dark-mode overlays. */
export function white(opacity: number): string {
  return `rgba(255,255,255,${opacity})`;
}

/** Black at a given opacity — for light-mode overlays. */
export function black(opacity: number): string {
  return `rgba(0,0,0,${opacity})`;
}

/** True when the mode is dark (for recipes branching on mode). */
function isDark(tokens: ThemeTokens): boolean {
  return tokens.mode === "dark";
}

/** True when the mode is light. */
function isLight(tokens: ThemeTokens): boolean {
  return tokens.mode === "light";
}


// ══════════════════════════════════════════════════════════════════════
// 7. RECIPES — curried: each takes tokens and returns StyleProps/StyleRecipe.
//
// Recipes are pure functions of their token argument. No module state,
// no setMode() escape hatch. Styles.svelte pre-binds them via bindRecipes()
// so components see zero-arg function calls at the context surface.
// ══════════════════════════════════════════════════════════════════════


// ── GLASS — 16 variants from the Slate Ink design prototype ────────

export const glass = {

  /** Default frosted — flat white-alpha fill. */
  frost(tokens: ThemeTokens): StyleProps {
    return {
      background:     tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Heavy frost — more white, more presence. */
  heavy(tokens: ThemeTokens): StyleProps {
    return {
      background:     isDark(tokens) ? white(0.07) : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.10)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Subtle mist — barely there. Used for header. */
  subtleMist(tokens: ThemeTokens): StyleProps {
    return {
      background:     isDark(tokens) ? white(0.025) : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.06)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Top-down — gradient white fading downward. */
  topDown(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(180, [white(0.06), "0%"], [white(0.02), "100%"])
        : tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Bottom-up — gradient white rising from bottom. */
  bottomUp(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(0, [white(0.06), "0%"], [white(0.015), "100%"])
        : tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Mixed — white top blending into hex dark bottom. */
  mixed(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(180, [white(0.05), "0%"], ["rgba(42,42,52,0.30)", "100%"])
        : tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Warm — lavender tint with saturate. */
  warm(tokens: ThemeTokens): StyleProps {
    return {
      background:     isDark(tokens) ? "rgba(240,235,255,0.04)" : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: isDark(tokens) ? backdrop(24, 1.2)            : tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Pure — no fill, border + blur only. */
  pure(tokens: ThemeTokens): StyleProps {
    return {
      background:     "transparent",
      border:         isDark(tokens) ? `0.5px solid ${white(0.06)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Center glow — radial white brightest at center. */
  centerGlow(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? radialGradient("50% 50%", [white(0.06), "0%"], [white(0.015), "100%"])
        : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Edge halo — transparent center, white at edges. */
  edgeHalo(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? radialGradient("50% 50%", ["transparent", "30%"], [white(0.04), "100%"])
        : tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Diagonal sweep — top-left to bottom-right. */
  diagonal(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(135, [white(0.06), "0%"], [white(0.01), "100%"])
        : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Corner spot — radial highlight from top-left. */
  cornerSpot(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? radialGradient("0% 0%", [white(0.07), "0%"], [white(0.015), "60%"], ["transparent", "100%"])
        : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Reverse hex — dark top, white-frost bottom. */
  reverseHex(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(180, ["rgba(42,42,52,0.25)", "0%"], [white(0.04), "100%"])
        : tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Horizontal split — left brighter, right subtle. */
  horizontal(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(90, [white(0.05), "0%"], [white(0.02), "100%"])
        : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Accent tint — faint blue top fading to neutral. */
  accentTint(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(180, ["rgba(96,165,250,0.03)", "0%"], [white(0.025), "100%"])
        : tokens.glass.fill,
      border:         isDark(tokens) ? `0.5px solid ${white(0.07)}` : tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },

  /** Original hex button gradient + blur. */
  hex(tokens: ThemeTokens): StyleProps {
    return {
      background:     HEX_GRADIENT.fill,
      border:         HEX_GRADIENT.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },
} as const;


// ── SURFACE ──────────────────────────────────────────────────────────

export const surface = {

  /** App background. */
  base(tokens: ThemeTokens): StyleProps {
    return { background: tokens.surface.base };
  },

  /** Sidebar background + right-edge border. */
  sidebar(tokens: ThemeTokens): StyleProps {
    return {
      background:  tokens.surface.sunken,
      borderRight: `0.5px solid ${tokens.border.standard}`,
    };
  },

  /** Sidebar border glow — for ::before pseudo-element. */
  sidebarGlow(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? gradient(270, [white(0.03), "0%"], ["transparent", "100%"])
        : "transparent",
      width: "20px",
    };
  },

  /** Sidebar blur layer — for ::after pseudo-element. */
  sidebarBlur(tokens: ThemeTokens): StyleProps {
    return {
      background:     "transparent",
      backdropFilter: tokens.glass.blur,
    };
  },

  /** Header — subtle mist glass over scrolling content. */
  header(tokens: ThemeTokens): StyleProps {
    return {
      background:     isDark(tokens) ? white(0.025) : tokens.surface.overlay,
      backdropFilter: tokens.glass.blur,
      borderBottom:   `0.5px solid ${tokens.border.standard}`,
      boxShadow:      tokens.shadow.header,
    };
  },

  /** Input area container. */
  input(tokens: ThemeTokens): StyleProps {
    return {
      background:   tokens.surface.raised,
      border:       `0.5px solid ${tokens.border.input}`,
      borderRadius: RADIUS.xl,
    };
  },

  /** Input area focused state. */
  inputFocused(tokens: ThemeTokens): StyleProps {
    return { borderColor: tokens.border.focus };
  },

  /** Flat card — inline content, not floating. */
  flat(tokens: ThemeTokens): StyleProps {
    return {
      background:   tokens.interactive.hover,
      border:       `0.5px solid ${tokens.border.subtle}`,
      borderRadius: RADIUS.lg,
    };
  },
} as const;


// ── BUTTON ───────────────────────────────────────────────────────────

export const button = {

  /** Primary — most prominent neutral button. */
  primary(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        background:   tokens.interactive.active,
        color:        tokens.text.primary,
        border:       `0.5px solid ${tokens.border.strong}`,
        borderRadius: RADIUS.md,
        height:       "36px",
      },
      hoverStyle: {
        background:  tokens.interactive.selected,
        borderColor: tokens.border.focus,
      },
    };
  },

  /** Secondary — neutral, lower emphasis. */
  secondary(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        background:   tokens.interactive.hover,
        color:        tokens.text.secondary,
        border:       `0.5px solid ${tokens.border.standard}`,
        borderRadius: RADIUS.md,
        height:       "36px",
      },
      hoverStyle: {
        background: tokens.interactive.active,
        color:      tokens.text.primary,
      },
    };
  },

  /** Ghost — no background, text only. */
  ghost(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        background:   "transparent",
        color:        tokens.text.secondary,
        border:       "none",
        borderRadius: RADIUS.md,
        height:       "36px",
      },
      hoverStyle: {
        color: tokens.text.primary,
      },
    };
  },

  /** Glass — frosted glass fill. */
  glass(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        ...glass.frost(tokens),
        color: tokens.text.primary,
      },
      hoverStyle: {
        background: tokens.glass.fillHover,
      },
    };
  },

  /** Icon button — header actions, toolbar (36px). */
  icon(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        width:        "36px",
        height:       "36px",
        borderRadius: RADIUS.md,
        background:   "transparent",
        color:        tokens.icon.idle,
        border:       "none",
      },
      hoverStyle: {
        background: tokens.interactive.hover,
        color:      tokens.icon.hover,
      },
      activeStyle: {
        background: tokens.interactive.active,
        color:      tokens.text.primary,
      },
    };
  },

  /** Tool button — input toolbar (30px). */
  tool(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        width:        "30px",
        height:       "30px",
        borderRadius: RADIUS.sm,
        background:   "transparent",
        color:        tokens.icon.idle,
        border:       "none",
      },
      hoverStyle: {
        background: tokens.interactive.hover,
        color:      tokens.icon.hover,
      },
      activeStyle: {
        background: tokens.interactive.active,
        color:      tokens.text.primary,
      },
    };
  },
} as const;


// ── AVATAR ───────────────────────────────────────────────────────────
// Avatar uses HEX_GRADIENT (mode-independent dark gradient) by design —
// that's the Slate Ink avatar look. Text color switches per mode so
// initials stay readable on light backgrounds.

export const avatar = {

  /** Active — full presence, default state. */
  active(tokens: ThemeTokens, size: number): StyleProps {
    return {
      width:        `${size}px`,
      height:       `${size}px`,
      borderRadius: "50%",
      background:   HEX_GRADIENT.fill,
      border:       `0.5px solid ${white(0.10)}`,
      color:        isDark(tokens) ? white(0.70) : white(0.85),
      fontWeight:   600,
    };
  },

  /** Away — subdued, stepped back. */
  away(tokens: ThemeTokens, size: number): StyleProps {
    return {
      width:        `${size}px`,
      height:       `${size}px`,
      borderRadius: "50%",
      background:   gradient(180, ["rgba(42,42,52,0.40)", "0%"], ["rgba(28,28,36,0.36)", "100%"]),
      border:       `0.5px solid ${white(0.05)}`,
      color:        isDark(tokens) ? white(0.30) : white(0.45),
      fontWeight:   600,
    };
  },

  /** Collab — skeleton shimmer, actively working. */
  collab(_tokens: ThemeTokens, size: number): StyleProps {
    return {
      width:        `${size}px`,
      height:       `${size}px`,
      borderRadius: "50%",
      background:   gradient(180, ["rgba(42,42,52,0.40)", "0%"], ["rgba(28,28,36,0.36)", "100%"]),
      border:       `0.5px solid ${white(0.05)}`,
      color:        "transparent",
      overflow:     "hidden",
    };
  },
} as const;


// ── INTERACTIVE STATES ───────────────────────────────────────────────

export const state = {

  idle(tokens: ThemeTokens): StyleProps {
    return {
      background: "transparent",
      border:     `0.5px solid ${tokens.border.subtle}`,
      color:      tokens.text.secondary,
    };
  },

  hover(tokens: ThemeTokens): StyleProps {
    return {
      background: tokens.interactive.hover,
      border:     `0.5px solid ${tokens.border.standard}`,
      color:      tokens.text.primary,
    };
  },

  active(tokens: ThemeTokens): StyleProps {
    return {
      background: tokens.interactive.active,
      border:     `0.5px solid ${tokens.border.strong}`,
      color:      tokens.text.primary,
    };
  },

  selected(tokens: ThemeTokens): StyleProps {
    return {
      background: tokens.interactive.selected,
      border:     `0.5px solid ${tokens.border.standard}`,
      color:      tokens.text.primary,
    };
  },

  focused(tokens: ThemeTokens): StyleProps {
    return {
      background:    "transparent",
      border:        `0.5px solid ${tokens.border.focus}`,
      color:         tokens.text.primary,
      outline:       `2px solid ${tokens.border.focus}`,
      outlineOffset: "1px",
    };
  },

  disabled(tokens: ThemeTokens): StyleProps {
    return {
      background: "transparent",
      border:     `0.5px solid ${tokens.border.subtle}`,
      color:      tokens.text.ghost,
      opacity:    0.5,
      cursor:     "not-allowed",
    };
  },
} as const;


// ── SKELETON LOADING ─────────────────────────────────────────────────

export const skeleton = {

  line(tokens: ThemeTokens): StyleProps {
    return {
      height:       "10px",
      borderRadius: RADIUS.xs,
      background:   tokens.interactive.hover,
      overflow:     "hidden",
    };
  },

  shimmer(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens)
        ? `linear-gradient(90deg, transparent 0%, ${white(0.04)} 50%, transparent 100%)`
        : `linear-gradient(90deg, transparent 0%, ${black(0.03)} 50%, transparent 100%)`,
      animation: "skeleton-shimmer 1.8s ease-in-out infinite",
    };
  },

  collabShimmer(_tokens: ThemeTokens): StyleProps {
    return {
      background: `linear-gradient(90deg, transparent 0%, ${white(0.04)} 50%, transparent 100%)`,
      animation:  "av-shimmer 3s ease-in-out infinite",
    };
  },
} as const;


// ── NAV ──────────────────────────────────────────────────────────────

export const nav = {

  /** Sidebar nav item. */
  item(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        height:       "40px",
        borderRadius: RADIUS.md,
        fontSize:     "13.5px",
        color:        tokens.text.secondary,
      },
      hoverStyle: {
        background: tokens.interactive.hover,
        color:      tokens.text.primary,
      },
      activeStyle: {
        background: tokens.interactive.active,
        color:      tokens.text.primary,
      },
    };
  },

  /** Sidebar list item (recent articles, scores, etc). */
  listItem(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        borderRadius: RADIUS.md,
        fontSize:     "13px",
        color:        tokens.text.secondary,
      },
      hoverStyle: {
        background: tokens.interactive.hover,
        color:      tokens.text.primary,
      },
    };
  },
} as const;


// ── DROPDOWN ─────────────────────────────────────────────────────────

export const dropdown = {

  container(tokens: ThemeTokens): StyleProps {
    return {
      ...glass.frost(tokens),
      padding:   SPACE[1],
      minWidth:  "200px",
      boxShadow: isLight(tokens) ? tokens.shadow.lg : "none",
    };
  },

  item(tokens: ThemeTokens): StyleRecipe {
    return {
      style: {
        borderRadius: RADIUS.sm,
        fontSize:     "13.5px",
        color:        tokens.text.secondary,
      },
      hoverStyle: {
        background: tokens.interactive.hover,
        color:      tokens.text.primary,
      },
    };
  },

  separator(tokens: ThemeTokens): StyleProps {
    return {
      height:     "0.5px",
      background: tokens.border.standard,
      margin:     `${SPACE[1]} ${SPACE[1]}`,
    };
  },
} as const;


// ── BRAND ────────────────────────────────────────────────────────────
// Generic styling for brand-name / tagline display. The actual text
// is supplied by the consuming component — these recipes only control style.

export const brand = {

  /** Primary brand name text style. */
  name(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:      "18px",
      fontWeight:    600,
      letterSpacing: "-0.02em",
      color:         tokens.text.primary,
    };
  },

  /** Tagline / sub-brand text style (e.g. "by LQD Research"). */
  tagline(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:   "12px",
      fontWeight: 400,
      color:      tokens.text.tertiary,
      marginLeft: "6px",
    };
  },

  /** Tagline color on hover of parent brand container. */
  hover(tokens: ThemeTokens): StyleProps {
    return { color: tokens.text.secondary };
  },
} as const;


// ── SCROLL WHEEL ─────────────────────────────────────────────────────

export const scrollWheel = {

  /** Tick mark style. */
  tick(tokens: ThemeTokens): StyleProps {
    return {
      height:       `${SCROLL_WHEEL.tickHeight}px`,
      left:         `${SCROLL_WHEEL.tickInset}px`,
      right:        `${SCROLL_WHEEL.tickInset}px`,
      borderRadius: "999px",
      background:   isDark(tokens) ? white(0.10) : black(0.10),
    };
  },

  /** Wheel body dimensions. */
  body(_tokens: ThemeTokens): StyleProps {
    return {
      width:  `${SCROLL_WHEEL.width}px`,
      height: `${SCROLL_WHEEL.height}px`,
    };
  },

  /** Physics constants for the wheel engine. */
  physics(_tokens: ThemeTokens) {
    return SCROLL_WHEEL;
  },
} as const;


// ── TYPOGRAPHY ───────────────────────────────────────────────────────

export const type = {

  xl(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:      "24px",
      fontWeight:    600,
      letterSpacing: "-0.025em",
      color:         tokens.text.primary,
    };
  },

  lg(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:      "18px",
      fontWeight:    600,
      letterSpacing: "-0.02em",
      color:         tokens.text.primary,
    };
  },

  md(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:   "15px",
      fontWeight: 500,
      color:      tokens.text.primary,
    };
  },

  body(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:   "14px",
      fontWeight: 400,
      color:      tokens.text.secondary,
      lineHeight: TYPE.leading,
    };
  },

  sm(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:   "12px",
      fontWeight: 400,
      color:      tokens.text.tertiary,
    };
  },

  xs(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:       "11px",
      fontWeight:     500,
      letterSpacing:  "0.04em",
      textTransform:  "uppercase",
      color:          tokens.text.ghost,
    };
  },

  label(tokens: ThemeTokens): StyleProps {
    return {
      fontSize:       "11px",
      fontWeight:     500,
      letterSpacing:  "0.06em",
      textTransform:  "uppercase",
      color:          tokens.text.tertiary,
    };
  },
} as const;


// ── TABLE ────────────────────────────────────────────────────────────

export const table = {

  container(tokens: ThemeTokens): StyleProps {
    return {
      background:     HEX_GRADIENT.fill,
      border:         HEX_GRADIENT.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.xl,
    };
  },

  toolbar(tokens: ThemeTokens): StyleProps {
    return {
      background:   HEX_GRADIENT.fill,
      borderBottom: `0.5px solid ${tokens.border.subtle}`,
    };
  },

  colHead(tokens: ThemeTokens): StyleProps {
    return {
      background:     tokens.interactive.hover,
      backdropFilter: "blur(20px)",
      borderRight:    `0.5px solid ${tokens.border.subtle}`,
      borderBottom:   `0.5px solid ${tokens.border.standard}`,
    };
  },

  colHeadHover(tokens: ThemeTokens): StyleProps {
    return {
      background:  tokens.interactive.active,
      borderColor: tokens.border.strong,
    };
  },

  colHeadSelected(tokens: ThemeTokens): StyleProps {
    return {
      background:   tokens.interactive.active,
      border:       `1.25px solid ${tokens.accent.solid}`,
      borderRadius: RADIUS.sm,
      boxShadow:    `0 0 6px ${tokens.accent.glow}`,
    };
  },

  colHeadSelectedHover(tokens: ThemeTokens): StyleProps {
    return {
      background:   tokens.accent.subtle,
      border:       `1.25px solid ${tokens.accent.solid}`,
      borderRadius: RADIUS.sm,
      boxShadow:    `0 0 12px ${isDark(tokens) ? "rgba(96,165,250,0.12)" : "rgba(58,110,245,0.10)"}`,
    };
  },

  rowNum(tokens: ThemeTokens): StyleProps {
    return {
      background:     tokens.interactive.hover,
      backdropFilter: "blur(20px)",
      borderRight:    `0.5px solid ${tokens.border.subtle}`,
      borderBottom:   `0.5px solid ${tokens.border.subtle}`,
      color:          tokens.text.secondary,
    };
  },

  cellEven(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens) ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
    };
  },

  cellOdd(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens) ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.04)",
    };
  },

  cellHighlight(tokens: ThemeTokens): StyleProps {
    return {
      background: isDark(tokens) ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
    };
  },

  selectionOverlay(tokens: ThemeTokens): StyleProps {
    return {
      border:       `1.25px solid ${tokens.accent.solid}`,
      borderRadius: RADIUS.sm,
      boxShadow:    `0 0 6px ${tokens.accent.glow}`,
    };
  },

  activeShimmer(tokens: ThemeTokens): StyleProps {
    return {
      background: `linear-gradient(90deg, transparent 0%, ${tokens.interactive.active} 50%, transparent 100%)`,
      animation:  "skeleton-shimmer 2.4s ease-in-out infinite",
    };
  },

  statStrip(tokens: ThemeTokens): StyleProps {
    return {
      background:   tokens.interactive.hover,
      borderTop:    `0.5px solid ${tokens.border.subtle}`,
      borderBottom: `0.5px solid ${tokens.border.subtle}`,
    };
  },

  sheetTab(tokens: ThemeTokens): StyleProps {
    return {
      borderRadius: RADIUS.sm,
      color:        tokens.text.secondary,
    };
  },

  sheetTabActive(tokens: ThemeTokens): StyleProps {
    return {
      background: tokens.interactive.active,
      color:      tokens.text.primary,
    };
  },

  dashPanel(tokens: ThemeTokens): StyleProps {
    return {
      background:     tokens.glass.fill,
      border:         tokens.glass.border,
      backdropFilter: tokens.glass.blur,
      borderRadius:   RADIUS.lg,
    };
  },
} as const;


// ── SELECTION ────────────────────────────────────────────────────────

/** Text selection colors — returns the SelectionTokens from the active mode. */
export function selection(tokens: ThemeTokens): SelectionTokens {
  return tokens.selection;
}


// ══════════════════════════════════════════════════════════════════════
// 8. bindRecipes — produces a BoundRecipes object with zero-arg calls.
//
// Styles.svelte calls this in a $derived whenever the active token set
// changes, and exposes the result via useStyles() context. Components
// call recipes.glass.frost() with no arguments — the tokens are baked in.
//
// Pure function. No side effects. Same tokens → same BoundRecipes structure.
// ══════════════════════════════════════════════════════════════════════

export function bindRecipes(tokens: ThemeTokens): BoundRecipes {
  return {
    glass: {
      frost:      () => glass.frost(tokens),
      heavy:      () => glass.heavy(tokens),
      subtleMist: () => glass.subtleMist(tokens),
      topDown:    () => glass.topDown(tokens),
      bottomUp:   () => glass.bottomUp(tokens),
      mixed:      () => glass.mixed(tokens),
      warm:       () => glass.warm(tokens),
      pure:       () => glass.pure(tokens),
      centerGlow: () => glass.centerGlow(tokens),
      edgeHalo:   () => glass.edgeHalo(tokens),
      diagonal:   () => glass.diagonal(tokens),
      cornerSpot: () => glass.cornerSpot(tokens),
      reverseHex: () => glass.reverseHex(tokens),
      horizontal: () => glass.horizontal(tokens),
      accentTint: () => glass.accentTint(tokens),
      hex:        () => glass.hex(tokens),
    },
    surface: {
      base:         () => surface.base(tokens),
      sidebar:      () => surface.sidebar(tokens),
      sidebarGlow:  () => surface.sidebarGlow(tokens),
      sidebarBlur:  () => surface.sidebarBlur(tokens),
      header:       () => surface.header(tokens),
      input:        () => surface.input(tokens),
      inputFocused: () => surface.inputFocused(tokens),
      flat:         () => surface.flat(tokens),
    },
    button: {
      primary:   () => button.primary(tokens),
      secondary: () => button.secondary(tokens),
      ghost:     () => button.ghost(tokens),
      glass:     () => button.glass(tokens),
      icon:      () => button.icon(tokens),
      tool:      () => button.tool(tokens),
    },
    avatar: {
      active: (size: number) => avatar.active(tokens, size),
      away:   (size: number) => avatar.away(tokens, size),
      collab: (size: number) => avatar.collab(tokens, size),
    },
    state: {
      idle:     () => state.idle(tokens),
      hover:    () => state.hover(tokens),
      active:   () => state.active(tokens),
      selected: () => state.selected(tokens),
      focused:  () => state.focused(tokens),
      disabled: () => state.disabled(tokens),
    },
    skeleton: {
      line:          () => skeleton.line(tokens),
      shimmer:       () => skeleton.shimmer(tokens),
      collabShimmer: () => skeleton.collabShimmer(tokens),
    },
    nav: {
      item:     () => nav.item(tokens),
      listItem: () => nav.listItem(tokens),
    },
    dropdown: {
      container: () => dropdown.container(tokens),
      item:      () => dropdown.item(tokens),
      separator: () => dropdown.separator(tokens),
    },
    brand: {
      name:    () => brand.name(tokens),
      tagline: () => brand.tagline(tokens),
      hover:   () => brand.hover(tokens),
    },
    scrollWheel: {
      tick:    () => scrollWheel.tick(tokens),
      body:    () => scrollWheel.body(tokens),
      physics: () => scrollWheel.physics(tokens),
    },
    type: {
      xl:    () => type.xl(tokens),
      lg:    () => type.lg(tokens),
      md:    () => type.md(tokens),
      body:  () => type.body(tokens),
      sm:    () => type.sm(tokens),
      xs:    () => type.xs(tokens),
      label: () => type.label(tokens),
    },
    table: {
      container:            () => table.container(tokens),
      toolbar:              () => table.toolbar(tokens),
      colHead:              () => table.colHead(tokens),
      colHeadHover:         () => table.colHeadHover(tokens),
      colHeadSelected:      () => table.colHeadSelected(tokens),
      colHeadSelectedHover: () => table.colHeadSelectedHover(tokens),
      rowNum:               () => table.rowNum(tokens),
      cellEven:             () => table.cellEven(tokens),
      cellOdd:              () => table.cellOdd(tokens),
      cellHighlight:        () => table.cellHighlight(tokens),
      selectionOverlay:     () => table.selectionOverlay(tokens),
      activeShimmer:        () => table.activeShimmer(tokens),
      statStrip:            () => table.statStrip(tokens),
      sheetTab:             () => table.sheetTab(tokens),
      sheetTabActive:       () => table.sheetTabActive(tokens),
      dashPanel:            () => table.dashPanel(tokens),
    },
    selection: () => selection(tokens),
  };
}
